import React, { useState, useEffect } from 'react';
import { ISLAND_SHAPES } from '../terrain/TerrainConstants.js';
import IslandTypeManager from '../terrain/IslandTypeManager.js';

const styles = {
  container: {
    position: 'absolute',
    top: '10px',
    right: '10px',
    background: 'rgba(0, 0, 0, 0.7)',
    padding: '10px',
    borderRadius: '5px',
    color: 'white',
    fontFamily: 'Arial, sans-serif',
    zIndex: 1001,
    boxShadow: '0 0 10px rgba(0, 0, 0, 0.5)',
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    maxWidth: '300px',
    pointerEvents: 'auto',
    cursor: 'auto'
  },
  input: {
    background: 'rgba(255, 255, 255, 0.9)',
    border: '1px solid #ddd',
    borderRadius: '3px',
    padding: '5px',
    width: '100%',
    boxSizing: 'border-box'
  },
  button: {
    background: '#4CAF50',
    color: 'white',
    border: 'none',
    borderRadius: '3px',
    padding: '8px 15px',
    cursor: 'pointer',
    fontWeight: 'bold',
    transition: 'background 0.2s'
  },
  infoText: {
    fontSize: '12px',
    opacity: 0.8,
    marginTop: '5px'
  },
  buttonGroup: {
    display: 'flex',
    gap: '5px'
  },
  selectGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '5px'
  },
  select: {
    background: 'rgba(255, 255, 255, 0.9)',
    border: '1px solid #ddd',
    borderRadius: '3px',
    padding: '5px'
  },
  hidden: {
    display: 'none'
  },
  toggleButton: {
    position: 'absolute',
    top: '10px',
    right: '10px',
    background: 'rgba(0, 0, 0, 0.7)',
    color: 'white',
    border: 'none',
    borderRadius: '3px',
    padding: '5px 10px',
    cursor: 'pointer',
    zIndex: 1000
  },
  slider: {
    width: '100%',
    margin: '5px 0'
  },
  sliderLabel: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '12px'
  }
};

const ISLAND_SHAPE_NAMES = {
  0: 'Circular Island',
  1: 'Irregular Island',
  2: 'Crescent Island',
  3: 'Volcanic Island',
  4: 'Atoll Island',
  5: 'Archipelago'
};

const WorldSeedUI = () => {
  const [seed, setSeed] = useState('');
  const [currentSeed, setCurrentSeed] = useState('');
  const [islandType, setIslandType] = useState('');
  const [currentIslandType, setCurrentIslandType] = useState(0);
  const [isEditing, setIsEditing] = useState(false);
  
  // Track game state
  const [isPaused, setIsPaused] = useState(false);
  const [isGameOver, setIsGameOver] = useState(false);
  
  useEffect(() => {
    // Get current world settings from URL and window.worldSeed
    const urlParams = new URLSearchParams(window.location.search);
    const urlSeed = urlParams.get('seed');
    const urlIslandType = urlParams.get('islandType');
    const forceParam = urlParams.get('_t');
    
    // Set current seed from window.worldSeed or URL
    if (window.worldSeed) {
      setCurrentSeed(window.worldSeed.toString());
    } else if (urlSeed) {
      setCurrentSeed(urlSeed);
    }

    // Get the current island type from the IslandTypeManager
    const islandManager = IslandTypeManager.getInstance();
    setCurrentIslandType(islandManager.getType());
    
    // Register as a listener for island type changes
    islandManager.addListener((newType) => {
      setCurrentIslandType(newType);
    });
    
    // Listen for game state changes
    const checkGameState = () => {
      if (typeof window.gameState !== 'undefined') {
        setIsPaused(window.gameState.isPaused);
      }
      const deathMessages = Array.from(document.querySelectorAll('div')).filter(div => 
        div.textContent === 'GAME OVER'
      );
      setIsGameOver(deathMessages.length > 0);
    };
    
    const interval = setInterval(checkGameState, 100);
    return () => clearInterval(interval);
  }, []);
  
  const regenerateWorld = async () => {
    try {
      const url = new URL(window.location);
      
      // Clear any existing parameters
      url.search = '';
      
      // Get the island manager reference
      const islandManager = IslandTypeManager.getInstance();
      
      // Only add parameters if we're forcing specific values
      if (seed) {
        const numericSeed = parseInt(seed);
        const finalSeed = isNaN(numericSeed) ? seed : numericSeed;
        url.searchParams.set('seed', finalSeed);
      }
      
      if (islandType !== '') {
        const typeNum = parseInt(islandType);
        url.searchParams.set('islandType', typeNum);
        
        // Important: Set the island type in the manager first - this must happen before reload
        if (!isNaN(typeNum) && typeNum >= 0 && typeNum <= 5) {
          console.log(`[WorldSeedUI] Setting island type to ${typeNum} (${ISLAND_SHAPE_NAMES[typeNum]})`);
          islandManager.setType(typeNum);
        }
      }
      
      // Always add force parameter when generating from UI
      url.searchParams.set('_t', Date.now());
      
      // Update URL and reload
      window.history.replaceState({}, '', url.toString());
      window.location.reload();
    } catch (error) {
      console.error("Error generating world:", error);
      alert("There was an error generating the world. Please try again.");
    }
  };
  
  const generateRandomSeed = () => {
    const newSeed = Math.floor(Math.random() * 1000000);
    setSeed(newSeed.toString());
    setIsEditing(true);
  };
  
  const handleIslandTypeChange = (e) => {
    const selectedType = e.target.value;
    setIslandType(selectedType);
    setIsEditing(true);
  };
  
  const handleInputChange = (e) => {
    setSeed(e.target.value);
    setIsEditing(true);
  };
  
  const handleInputBlur = () => {
    if (seed.trim() === '') {
      setSeed('');
      setIsEditing(false);
    }
  };
  
  // Only show UI when game is paused or game over
  if (!isPaused && !isGameOver) {
    return null;
  }
  
  return (
    <div style={styles.container}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h3 style={{ margin: '0 0 10px 0' }}>World Generation</h3>
      </div>
      
      <div>
        <div>Current World Seed: {currentSeed}</div>
        <div>Current Island Type: {ISLAND_SHAPE_NAMES[currentIslandType]}</div>
      </div>
      
      <div style={styles.selectGroup}>
        <label>Select New Island Type:</label>
        <select 
          style={styles.select} 
          value={islandType}
          onChange={handleIslandTypeChange}
        >
          <option value="">Random</option>
          {Object.entries(ISLAND_SHAPE_NAMES).map(([key, value]) => (
            <option key={key} value={key}>{value}</option>
          ))}
        </select>
      </div>
      
      <div>
        <label>New Seed (optional):</label>
        <input 
          type="text" 
          style={styles.input} 
          value={seed}
          onChange={handleInputChange}
          onBlur={handleInputBlur}
          placeholder="Enter seed number or text"
        />
      </div>
      
      <div style={styles.buttonGroup}>
        <button 
          style={styles.button} 
          onClick={regenerateWorld}
        >
          Generate World
        </button>
        <button 
          style={{...styles.button, background: '#2196F3'}} 
          onClick={generateRandomSeed}
        >
          Random Seed
        </button>
      </div>
      
      <div style={styles.infoText}>
        Leave empty for random generation. Click Generate World to apply changes.
      </div>
    </div>
  );
};

export default WorldSeedUI; 